import {
  MutationCache,
  QueryClient,
  dehydrate,
  hydrate,
  DehydratedState,
  onlineManager,
} from '@tanstack/react-query';
import { ReactNode, useEffect } from 'react';
import {
  PersistQueryClientProvider,
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import jwtDecode from 'jwt-decode';
import storageLocal from 'common/infrastructure/storageLocal/LocalForage';
import mutationProvider from './mutation.provider';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { isTokenExpirationValid } from 'identityAndAccess/infrastructure/utils';
import { isUnreachableError } from './mutations.interface';
import { Tp2Id, Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';

const RECHECK_INTERVAL_MS = 1000;
const RECHECK_TIMEOUT_MS = 5000;

let recheckTimer: ReturnType<typeof setTimeout> | undefined;
let probing = false;

function applyNavigatorOnline(): void {
  onlineManager.setOnline(typeof navigator === 'undefined' || navigator.onLine !== false);
}

/**
 * Same-origin so it cannot be answered from cache and stays invisible to the e2e network
 * assertions, which only watch the API host. It proves the network is usable, not that the
 * API is up — if only the API is down the resumed mutation fails and marks us unreachable
 * again, which degrades to polling rather than resuming wrongly.
 */
async function isNetworkReachable(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), RECHECK_TIMEOUT_MS);
  try {
    await fetch(`/index.html?connectivity=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

/** A fixed interval, and `probing` guards the window where the timer handle is already
 *  cleared but the probe is still in flight, so probes can never overlap. */
function scheduleRecheck(): void {
  if (recheckTimer || probing) return;

  recheckTimer = setTimeout(() => {
    recheckTimer = undefined;
    void recheckReachability();
  }, RECHECK_INTERVAL_MS);
}

async function recheckReachability(): Promise<void> {
  if (probing) return;
  probing = true;
  try {
    // navigator.onLine is honest about being offline and the browser fires 'online' on the
    // way back, so there is nothing to poll for.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    if (await isNetworkReachable()) {
      onlineManager.setOnline(true);
      return;
    }
  } finally {
    probing = false;
  }

  scheduleRecheck();
}

/**
 * A response-less failure is firsthand evidence the network is gone, even when
 * navigator.onLine still claims otherwise. Marking offline parks queued mutations so the
 * persister keeps them; recovery is then driven by the probe above, because a lying
 * navigator never fires an 'online' event to recover from.
 */
function markUnreachable(): void {
  onlineManager.setOnline(false);
  scheduleRecheck();
}

/**
 * Same signal for work that is not a TanStack mutation — the photo-link drain and the work
 * submission runner are plain async calls, so their failures never reach the MutationCache
 * and would otherwise leave us believing we are online with nothing to re-probe.
 * Returns true when the error was a connectivity failure.
 */
export function reportUnreachableError(error: unknown): boolean {
  if (!isUnreachableError(error)) return false;
  markUnreachable();
  return true;
}

function startReachabilityTracking(): () => void {
  onlineManager.setEventListener(() => {
    const onOffline = () => onlineManager.setOnline(false);
    const onOnline = () => applyNavigatorOnline();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') applyNavigatorOnline();
    };

    applyNavigatorOnline();
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  });

  return () => {
    clearTimeout(recheckTimer);
    recheckTimer = undefined;
  };
}

/** Permanent failure of a submission queued while offline; no caller is awaiting it. A
 *  mutation that never paused was awaited by its caller, whose own catch reports it. */
export interface QueuedFailure {
  kind: 'submission' | 'photoLink';
  message: string;
  /** tp2 asset id when the variables carry one, else the tp3 id, for the failure toast. */
  reference?: Tp2Id | Tp3Id;
}

const PHOTO_MUTATION_KEYS = [
  'uploadPhotoToAsset',
  'uploadPhotoToWorkTask',
  'uploadPhotoToInspection',
];

function describeFailedMutation(mutation: {
  options: { mutationKey?: unknown };
  state: { variables?: unknown };
}): Pick<QueuedFailure, 'kind' | 'reference'> {
  const { mutationKey } = mutation.options;
  const name = Array.isArray(mutationKey) ? String(mutationKey[0]) : '';
  const variables = mutation.state.variables as
    | { tp2AssetId?: Tp2Id; assetId?: Tp3Id; entityId?: Tp3Id }
    | undefined;

  return {
    kind: PHOTO_MUTATION_KEYS.includes(name) ? 'photoLink' : 'submission',
    reference: variables?.tp2AssetId ?? variables?.assetId ?? variables?.entityId,
  };
}

const everPausedMutations = new WeakSet<object>();
const queuedFailureListeners = new Set<(failure: QueuedFailure) => void>();

export function subscribeQueuedSubmissionFailures(
  listener: (failure: QueuedFailure) => void
): () => void {
  queuedFailureListeners.add(listener);
  return () => {
    queuedFailureListeners.delete(listener);
  };
}

export function emitQueuedSubmissionFailure(failure: QueuedFailure): void {
  queuedFailureListeners.forEach((listener) => listener(failure));
}

// km 1.30.23. i believe this has to be the same QueryClient instance for all
//   calls so they are available to persist in localForage
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 60 * 1000, // 60 seconds
      refetchOnWindowFocus: false,
      retry: (failureCount: number, error: unknown) => {
        if (error) {
          const errorString = String(error);
          if (errorString.includes('Access forbidden')) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
  },
  // Configure global cache callbacks
  mutationCache: new MutationCache({
    onSuccess: (data) => {
      loggerService.log(LoggerLevel.INFO, 'Mutation complete');
    },
    onError: (error, _variables, _context, mutation) => {
      loggerService.log(LoggerLevel.ERROR, 'Mutation error', error);
      if (mutation && everPausedMutations.has(mutation)) {
        emitQueuedSubmissionFailure({
          ...describeFailedMutation(mutation),
          message: error instanceof Error ? error.message : String(error),
        });
      }
    },
  }),
});

queryClient.getMutationCache().subscribe((event) => {
  if (event.mutation?.state.isPaused) everPausedMutations.add(event.mutation);

  // A response-less failure is firsthand evidence the network is gone; marking offline
  // lets the retryer park the mutation, which is what the persister writes to disk.
  const action = 'action' in event ? event.action : undefined;
  if (action?.type === 'failed' && isUnreachableError(action.error)) {
    markUnreachable();
  }
});

/**
 * Generates a cache key that includes app version for automatic cache busting on updates.
 * Tenant/user isolation is handled by:
 * - Query keys containing tenant IDs
 * - clearReactQueryCache() on logout and tenant switch
 * - shouldDehydrateQuery filtering asset queries
 */
export function getCacheKey(): string {
  const appVersion = window.appVersion || 'unknown';
  return `rq::${appVersion}`;
}

/**
 * Removes orphaned cache entries from previous app versions.
 * When the app version changes, old cache entries with different version keys
 * remain in LocalForage storage, causing storage bloat. This function cleans up
 * all old 'rq::' prefixed keys that don't match the current cache key.
 */
export async function cleanupOldCaches(): Promise<void> {
  try {
    const currentKey = getCacheKey();
    const allKeys = await storageLocal.getStorageInstance().keys();

    const oldCacheKeys = allKeys.filter(
      (key: string) => key.startsWith('rq::') && key !== currentKey
    );

    if (oldCacheKeys.length > 0) {
      await Promise.all(oldCacheKeys.map((key: string) => storageLocal.removeItem(key)));
      loggerService.log(LoggerLevel.INFO, `Cleaned up ${oldCacheKeys.length} old cache entries`, {
        oldKeys: oldCacheKeys,
      });
    }
  } catch (error) {
    loggerService.log(LoggerLevel.ERROR, 'Failed to cleanup old caches', error);
  }
}

// ---------------------------------------------------------------------------
// Pending mutation preservation across cache-clearing events (logout, version update)
// ---------------------------------------------------------------------------

const PENDING_MUTATIONS_KEY_PREFIX = 'pending-mutations::';

/**
 * Decodes the stored idToken and extracts the Cognito `sub` to produce a
 * user-scoped storage key. Returns null if no token exists or decoding fails.
 */
function getPendingMutationsStorageKey(): string | null {
  try {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) return null;
    const decoded = jwtDecode<{ sub?: string }>(idToken);
    return decoded.sub ? `${PENDING_MUTATIONS_KEY_PREFIX}${decoded.sub}` : null;
  } catch {
    return null;
  }
}

/**
 * Returns true only if the stored idToken exists and has not expired.
 * Used to guard mutation restoration — we should not restore and immediately
 * fire mutations with an expired JWT.
 */
function isStoredTokenValid(): boolean {
  const idToken = localStorage.getItem('idToken');
  return idToken ? isTokenExpirationValid(idToken) : false;
}

/**
 * Captures all currently-paused mutations and writes them to a separate,
 * user-scoped IndexedDB key so they survive cache clearing (logout, version update).
 *
 * Must be called BEFORE queryClient.clear() — reads from the in-memory MutationCache.
 * Writes directly to storageLocal (bypasses the persister, so persister enable/disable
 * state has no effect here).
 */
export async function savePausedMutations(): Promise<void> {
  const key = getPendingMutationsStorageKey();
  if (!key) {
    loggerService.log(LoggerLevel.DEBUG, 'savePausedMutations: no user key available, skipping');
    return;
  }

  const dehydrated: DehydratedState = dehydrate(queryClient, {
    shouldDehydrateQuery: () => false,
    shouldDehydrateMutation: (mutation) => mutation.state.isPaused,
  });

  if (dehydrated.mutations.length === 0) {
    loggerService.log(LoggerLevel.DEBUG, 'savePausedMutations: no paused mutations to preserve');
    return;
  }

  await storageLocal.setItem(key, JSON.stringify(dehydrated));
  loggerService.log(
    LoggerLevel.INFO,
    `savePausedMutations: preserved ${dehydrated.mutations.length} mutations`,
    { key }
  );
}

/**
 * Restores any paused mutations saved during a previous cache-clearing event,
 * then resumes them. Safe to call multiple times — deletes the key after first
 * restoration so subsequent calls are no-ops.
 *
 * Returns early (without deleting the key) if no JWT is present or if the
 * stored token is expired, so the mutations remain available for the next
 * valid session.
 *
 * Called from two places:
 *  1. RemoteStateProvider.onSuccess — handles page reloads where JWT is already
 *     in localStorage (e.g. ErrorPage Reset, normal session restore).
 *  2. AwsCognitoJwtContext.login() — handles the post-logout Cognito re-login
 *     path, where onSuccess already fired before the JWT was available.
 */
export async function restoreAndResumeSavedMutations(): Promise<void> {
  const key = getPendingMutationsStorageKey();
  if (!key) return; // No idToken in localStorage — skip silently

  if (!isStoredTokenValid()) {
    loggerService.log(
      LoggerLevel.DEBUG,
      'restoreAndResumeSavedMutations: token not valid, skipping restore until a valid JWT is available'
    );
    return; // Do NOT delete the key — mutations are preserved for when JWT is valid
  }

  try {
    const raw: string | undefined = await storageLocal.getItem(key);
    if (!raw) return;

    const savedState: DehydratedState = JSON.parse(raw);
    if (!savedState.mutations?.length) return;

    loggerService.log(
      LoggerLevel.INFO,
      `restoreAndResumeSavedMutations: restoring ${savedState.mutations.length} mutations`,
      { key }
    );

    hydrate(queryClient, savedState);
    await queryClient.resumePausedMutations();

    await storageLocal.removeItem(key);
    loggerService.log(LoggerLevel.DEBUG, 'restoreAndResumeSavedMutations: cleaned up storage key', {
      key,
    });
  } catch (error) {
    loggerService.log(
      LoggerLevel.ERROR,
      'restoreAndResumeSavedMutations: failed to restore mutations',
      error
    );
    try {
      await storageLocal.removeItem(key);
    } catch {
      // ignore secondary failure
    }
  }
}

interface PhotoData {
  name: string;
  id: string;
  dateUploaded: string;
  uploadStatus?: string;
  base64Data?: string;
}

interface QueryState {
  data?: {
    data?: PhotoData[];
  };
  dataUpdateCount: number;
  dataUpdatedAt: number;
  error: unknown;
  errorUpdateCount: number;
  errorUpdatedAt: number;
  fetchMeta: unknown;
  fetchStatus: string;
  isInvalidated: boolean;
  status: string;
  isPaused: boolean;
  isPlaceholderData: boolean;
  fetchFailureCount: number;
  fetchFailureReason: unknown;
}

/**
 * Creates a persister that uses dynamic cache keys partitioned by tenant/user/version.
 * Photo base64 data is stripped before persistence to reduce storage size.
 *
 * Supports pausing persistence during cache clearing operations to prevent
 * the persister from immediately re-saving the empty cache state.
 */
function createStorageLocalPersister() {
  let isEnabled = true;

  const persister = {
    persistClient: async (client: PersistedClient) => {
      // Skip persistence if disabled (e.g., during cache clearing)
      if (!isEnabled) {
        return;
      }

      const key = getCacheKey();
      if (client.clientState) {
        const queries = client.clientState.queries || [];
        const cleanedQueries = queries.map((query) => {
          if (
            query.queryKey &&
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'getAllPhotosByAsset'
          ) {
            const state = query.state as unknown as QueryState;
            if (state?.data?.data) {
              const cleanData = {
                ...state.data,
                data: state.data.data.map((photo) => {
                  // We need to destructure base64Data to remove it from the persisted data
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { base64Data, ...cleanPhoto } = photo;
                  return cleanPhoto;
                }),
              };
              return {
                ...query,
                state: {
                  ...state,
                  data: cleanData,
                },
              } as unknown as typeof query;
            }
          }
          return query;
        });

        client.clientState.queries = cleanedQueries;
      }

      await storageLocal.setItem(key, JSON.stringify(client));
    },
    restoreClient: async (): Promise<PersistedClient | undefined> => {
      const key = getCacheKey();
      const client: string | undefined = await storageLocal.getItem(key);
      return client ? JSON.parse(client) : undefined;
    },
    removeClient: async () => {
      const key = getCacheKey();
      await storageLocal.removeItem(key);
    },
    // Extension method for disabling persistence during cache clearing
    disable: () => {
      isEnabled = false;
    },
  };

  return persister as Persister & { disable: () => void };
}

export const persister = createStorageLocalPersister();

/**
 * Internal function that performs the complete React Query cache clearing sequence.
 * Used by both manual cache clearing and automatic cache busting on version updates.
 */
export const clearQueryClientCache = async (): Promise<void> => {
  // Save paused (offline-queued) mutations before clearing so they survive logout/version updates.
  try {
    await savePausedMutations();
  } catch (error) {
    loggerService.log(
      LoggerLevel.WARN,
      'Failed to preserve paused mutations before cache clear; continuing clear flow',
      error
    );
  }

  // Disable persistence to prevent re-saving during cache clear
  persister.disable();

  await queryClient.cancelQueries();
  loggerService.log(LoggerLevel.DEBUG, 'Cancelled in-flight queries');

  await queryClient.invalidateQueries({ refetchType: 'none' });
  loggerService.log(LoggerLevel.DEBUG, 'Invalidated all queries');

  queryClient.clear();
  loggerService.log(LoggerLevel.DEBUG, 'Cleared in-memory React Query cache');

  await persister.removeClient();
  loggerService.log(LoggerLevel.DEBUG, 'Removed persisted React Query cache');
};

/**
 * Clears all React Query cache data including in-memory cache and persisted storage.
 * Called manually from ErrorPage or other components that need to reset cache state.
 */
export const clearReactQueryCache = async (): Promise<void> => {
  await clearQueryClientCache();
};

mutationProvider.init(queryClient);

interface Props {
  children: ReactNode;
}

export default function RemoteStateProvider({ children }: Props) {
  useEffect(() => {
    const stopReachabilityTracking = startReachabilityTracking();
    cleanupOldCaches();
    return stopReachabilityTracking;
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
      onSuccess={async () => {
        if (navigator.onLine) {
          await queryClient.resumePausedMutations();
          await restoreAndResumeSavedMutations();
        }
        // If offline: mutations stay persisted as paused.
        // TanStack auto-resumes them when the browser fires the 'online' event.
      }}
    >
      {children}
      {process.env.NODE_ENV !== 'production' && <ReactQueryDevtools initialIsOpen={false} />}
    </PersistQueryClientProvider>
  );
}
